import { InfoIcon, WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useMemo, useState } from 'react'
import { useToastService } from '../../services/toast-service.tsx'
import {
  UNGROUPED_PROJECT_FILTER,
  useAddProjectMemberships,
  useGetRepositories,
} from '../../utils/api-client/api-hooks.ts'
import { getRepositoryProject, type Project } from '../../utils/api-client/api-schemas.ts'
import type { AvailableTemplate } from '../../utils/api-client/templates.ts'
import { joinWithAnd, pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { SearchInput } from '../input/search-input.tsx'
import { useShowProjectMutationError } from '../projects/admin-required-override.ts'
import { toPickableRepository } from '../projects/pickable-repositories.ts'
import { addSelectionLimit, isProjectFullError, projectFullMessage } from '../projects/project-capacity.ts'
import * as styles from '../projects/project-dialog.css.ts'
import { RepositoryPickerSection } from '../projects/repository-picker-section.tsx'
import { SelectionSummary } from '../projects/selection-summary.tsx'
import { SEARCH_PAGE_SIZE, useRepositoryPicker } from '../projects/use-repository-picker.ts'

export type AddRepositoriesModalProps = {
  project: Pick<Project, 'id' | 'name' | 'repository_capacity' | 'repository_count'>
  template: AvailableTemplate
  onClose: () => unknown
  onAdded: () => unknown
  /**
   * Returns focus to whatever opened this dialog. Radix restores to a `Dialog.Trigger`, and this
   * dialog has none — it mounts already open. @see useReturnFocus
   */
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * Adds repositories to a project. Everything not already in this project is on offer — the ungrouped
 * first, since adding one of those is the ordinary case and the rest cost another project a repository.
 */
export const AddRepositoriesModal: React.FC<AddRepositoriesModalProps> = ({
  project,
  template,
  onClose,
  onAdded,
  onCloseAutoFocus,
}) => {
  const { addToast } = useToastService()
  const [fullError, setFullError] = useState<string>()

  // The search is the API's: what the reader types goes to `GET /repositories?name=` rather than
  // narrowing a window fetched up front, so an organization with thousands of repositories searches
  // the same way as one with ten.
  //
  // "Ungrouped first" has to hold across the estate, not within one page, so the ungrouped are asked
  // for on their own. The API has no "grouped" filter, so the grouped ones come from an unfiltered
  // page, sized so that this project's members — its count, or its capacity if larger — cannot crowd
  // every other row out of it: an empty result after dropping them really does mean nothing else matched.
  const [search, setSearch] = useState('')
  const ungroupedQuery = useGetRepositories({
    pageIndex: 0,
    pageSize: SEARCH_PAGE_SIZE,
    searchTerm: search,
    projectIds: [UNGROUPED_PROJECT_FILTER],
  })
  const everyRepositoryQuery = useGetRepositories({
    pageIndex: 0,
    pageSize: SEARCH_PAGE_SIZE + Math.max(project.repository_capacity, project.repository_count),
    searchTerm: search,
    embedProjectMembership: true,
  })
  // Only for its total: the header counts what is on offer, which is everything matching less this
  // project's own members.
  const membersQuery = useGetRepositories({
    pageIndex: 0,
    pageSize: 1,
    searchTerm: search,
    projectIds: [project.id],
  })
  const candidates = useMemo(
    () =>
      [
        ...(ungroupedQuery.data?._embedded.items ?? []),
        ...(everyRepositoryQuery.data?._embedded.items ?? []).filter(repository => {
          const current = getRepositoryProject(repository)
          return current !== undefined && current.id !== project.id
        }),
      ].map(toPickableRepository),
    [ungroupedQuery.data, everyRepositoryQuery.data, project.id]
  )
  // Both totals have to answer the same search: while either still holds the previous one's, or the
  // members could not be counted, the header shows no count rather than a wrong one.
  const candidateTotal =
    everyRepositoryQuery.data &&
    membersQuery.data &&
    !everyRepositoryQuery.isPlaceholderData &&
    !membersQuery.isPlaceholderData &&
    !membersQuery.isError
      ? everyRepositoryQuery.data.total - membersQuery.data.total
      : undefined
  // The selection is bounded by the room left in THIS project, not by a flat hundred: the dialog stops
  // the overshoot where it happens rather than letting the server refuse the submission.
  const selectionLimit = addSelectionLimit(project)
  const picker = useRepositoryPicker({ cap: selectionLimit.cap })

  // Read off what is picked rather than off what is on screen: a search can take a picked repository
  // out of the list, and the warning still has to name every one that would be moved.
  const moving = useMemo(
    () => picker.pickedRepositories.filter(candidate => candidate.currentProjectName),
    [picker.pickedRepositories]
  )

  const showError = useShowProjectMutationError('Failed to add repositories')
  const assignMutation = useAddProjectMemberships({
    onSuccess: (_data, variables) => {
      addToast({
        message: (
          <>
            {variables.repositoryIds.length} {pluralize(variables.repositoryIds.length, 'repository', 'repositories')}{' '}
            added to &ldquo;{project.name}&rdquo;
          </>
        ),
        variant: 'success',
      })
      onAdded()
    },
    onError: (error, ...rest) => {
      // The remaining room above is derived from a count the client was handed, so it can be out of date.
      // The server is the one that knows: keep the dialog and the selection, and say what it said.
      if (isProjectFullError(error)) {
        setFullError(projectFullMessage(error))
        return
      }
      showError(error, ...rest)
    },
  })

  const pickedCount = picker.pickedIds.size
  const isPending = assignMutation.isPending

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onPointerDownOutside={event => event.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Dialog.Title className={styles.title}>Add repositories to {project.name}</Dialog.Title>
          <Dialog.Description hidden>Pick the repositories to add to this project.</Dialog.Description>

          <SearchInput ariaLabel="Search repositories" value={search} onChange={setSearch} />

          <RepositoryPickerSection
            label="Available repositories"
            filteredLabel="Matching repositories"
            emptyMessage="Every repository is already in this project."
            candidates={candidates}
            total={candidateTotal}
            pickedIds={picker.pickedIds}
            onToggle={picker.toggle}
            unpickedDisabledReason={picker.isAtSelectionCap ? selectionLimit.reason : undefined}
            filter={search}
            isFiltering={search.trim() !== ''}
            isPending={ungroupedQuery.isPending || everyRepositoryQuery.isPending || membersQuery.isPending}
            isError={ungroupedQuery.isError || everyRepositoryQuery.isError || membersQuery.isError}
          />

          {/* Always on: it describes the dialog, not the selection, so it should not pop in on the first tick. */}
          <div className={styles.infoNote}>
            <InfoIcon size={16} weight="regular" className={styles.noteIcon} aria-hidden />
            <span>Upon adding, repositories will adhere to this project&apos;s feedback.</span>
          </div>

          {moving.length > 0 && (
            <div className={styles.warningNote} role="status">
              <WarningIcon size={16} weight="fill" className={styles.noteIcon} aria-hidden />
              <span>
                {joinWithAnd(moving.map(candidate => candidate.fullName))} {moving.length > 1 ? 'are' : 'is'} already in
                a project. Adding {moving.length > 1 ? 'them' : 'it'} here <strong>moves</strong>{' '}
                {moving.length > 1 ? 'them' : 'it'}.
              </span>
            </div>
          )}

          {fullError && (
            <p className={styles.errorNote} role="alert">
              {fullError}
            </p>
          )}

          <div className={styles.bar}>
            <SelectionSummary selectedCount={pickedCount} onClear={picker.clearPicked} />
            <span className={styles.buttons}>
              <GhostButton
                type="secondary"
                className={styles.outlinedGhost}
                onClick={onClose}
                state={isPending ? 'disabled' : 'default'}
              >
                Cancel
              </GhostButton>
              <DefaultButton
                state={isPending ? 'loading' : pickedCount === 0 ? 'disabled' : 'default'}
                onClick={() =>
                  assignMutation.mutate({ projectId: project.id, template, repositoryIds: [...picker.pickedIds] })
                }
                loadingLabel="Adding repositories"
              >
                Add {pickedCount > 0 ? `${pickedCount} ` : ''}
                {pluralize(pickedCount, 'repository', 'repositories')}
              </DefaultButton>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
